import { createContext, useContext, useMemo } from 'react';

import {
  TConversionStatusBatchContext,
  TConversionStatusBatchProviderProps,
} from '@/console/features/projects/components/conversion-status-batch/conversion-status-batch-context.types';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadJobsByProjectIdsQuery } from '@/shared/redux/api/jobs/jobs-api';
import {
  FeatureEnum,
  JobFilterInput,
  JobFragment,
  ReadJobsByProjectIdsQuery,
} from '@/shared/types/types.generated';

const JOB_CONVERSION_STATUS_POLLING_INTERVAL_MS = 30_000;

// Consumers read at most the two newest jobs per version, but the response is
// one flat newest-first page shared by every version. Without headroom a single
// heavily re-converted version fills the page and starves the others, which
// surfaces as "not started" on versions that do have conversions.
const JOBS_PER_VERSION_HEADROOM = 8;

const ConversionStatusBatchContext =
  createContext<TConversionStatusBatchContext | null>(null);

export const bucketJobsByVersion = (
  jobs: JobFragment[],
): Map<string, JobFragment[]> => {
  const map = new Map<string, JobFragment[]>();
  for (const item of jobs) {
    for (const vid of item.projectVersionIds ?? []) {
      const existing = map.get(vid);
      if (existing) {
        existing.push(item);
      } else {
        map.set(vid, [item]);
      }
    }
  }
  return map;
};

const useVersionFilter = (versionIds: string[]): JobFilterInput | undefined =>
  useMemo(() => {
    if (versionIds.length === 0) return undefined;
    return {
      projectVersionIds: { containsAny: versionIds },
    };
  }, [versionIds]);

/**
 * Fetches conversion jobs for a page of project versions in one request.
 *
 * TODO: switch back to `useReadLatestJobsByProjectIdsQuery` once phoenix-jobs
 * applies the version filter before `limitPerProject` instead of after it. That
 * resolver is the cheaper one — a bounded query per project instead of draining
 * every conversion job of every project on the page — but today its Python-side
 * filter runs on whatever survived the window, so on a busy project the
 * conversion of the version we display is already gone and the row renders
 * "not started" while the detail page shows the real status. The operation and
 * its generated hook are kept in `jobs.queries.graphql` / `jobs-api.ts` so the
 * switch back needs no codegen. Details and the backend fix options:
 * SDA/CONVERSION_STATUS_FINDINGS.md §2.
 */
export const ConversionStatusBatchProvider = ({
  projectIds,
  versionIds,
  children,
}: TConversionStatusBatchProviderProps) => {
  const { isJobRunning } = useJobStatus();
  const filter = useVersionFilter(versionIds);
  const skip = projectIds.length === 0 || versionIds.length === 0;
  const pageSize = versionIds.length * JOBS_PER_VERSION_HEADROOM;

  const { currentData } = usePollingQuery({
    useQueryHook: useReadJobsByProjectIdsQuery,
    queryArg: {
      projectIds,
      jobTypes: [FeatureEnum.ProjectConvert],
      filter,
      pagination: {
        page: 1,
        pageSize,
      },
    },
    shouldStopPolling: (data: ReadJobsByProjectIdsQuery) =>
      data.readJobsByProjectIds.items.every(
        (item) => !isJobRunning(item.status),
      ),
    pollingInterval: JOB_CONVERSION_STATUS_POLLING_INTERVAL_MS,
    queryOptions: { skip },
  });

  // Keyed on contents, not identity: versionIds arrives as a fresh array every
  // render, and the context value has to stay referentially stable between
  // responses or every row re-renders whenever the list does.
  const versionIdsKey = versionIds.join(',');

  const jobsByVersion = useMemo(() => {
    // Nothing answered yet, so no version may be reported as "no executions".
    // Read currentData, never data: `data` keeps serving the previous args'
    // result while a new page is in flight, which would bucket the old page's
    // jobs under version ids nobody here asked about.
    if (!currentData) {
      return undefined;
    }

    const jobsPage = currentData.readJobsByProjectIds;
    const buckets = bucketJobsByVersion(jobsPage.items);
    const totalMatchingJobs = jobsPage.itemCount ?? jobsPage.items.length;
    const responseIsComplete =
      jobsPage.items.length < pageSize ||
      (totalMatchingJobs <= jobsPage.items.length &&
        (jobsPage.pages ?? 1) <= 1);

    if (responseIsComplete) {
      // Seed the versions this response covers. That is what separates
      // "answered: this version has no conversions" from "not asked about yet".
      versionIds.forEach((versionId) => {
        if (!buckets.has(versionId)) {
          buckets.set(versionId, []);
        }
      });
    }

    return buckets;
  }, [currentData, pageSize, versionIdsKey]);

  const value = useMemo(
    () => ({
      getJobsForVersion: (versionId: string) => jobsByVersion?.get(versionId),
    }),
    [jobsByVersion],
  );

  return (
    <ConversionStatusBatchContext.Provider value={value}>
      {children}
    </ConversionStatusBatchContext.Provider>
  );
};

export const useConversionStatusBatch = () =>
  useContext(ConversionStatusBatchContext);
